import React, { useState, useEffect, useCallback, useRef } from 'react';

const TUTORIAL_DONE_KEY = 'admin_tutorial_done';

export function shouldShowAdminTutorial() {
  return !localStorage.getItem(TUTORIAL_DONE_KEY);
}

interface Step {
  id: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  target?: string;
  waitForAction?: boolean;
  remeasureDelay?: number;
  onEnter?: () => void;
  actionLabel?: string;
}

const ADMIN_STEPS: Step[] = [
  {
    id: 'welcome',
    placement: 'center',
    title: '⚠️ Admin Panel',
    body: "Welcome to the Admin Panel. This is where you manage reports, help tickets, feedback, users and mod logs. Tap anywhere to continue.",
    actionLabel: "Let's go →",
  },
  {
    id: 'stats',
    target: '.admin-stats',
    placement: 'bottom',
    title: '📊 At a Glance',
    body: "These stats show your total open reports, restricted users and total user count. Tap anywhere to continue.",
    remeasureDelay: 300,
  },
  {
    id: 'tabs',
    target: '.admin-tabs',
    placement: 'bottom',
    title: '🗂️ Navigation Tabs',
    body: "Switch between Reports, Help tickets, Feedback, Users and Mod Logs here. Click the Help tab to continue.",
    waitForAction: true,
    remeasureDelay: 300,
  },
  {
    id: 'help-tab',
    target: '.admin-list, .admin-empty',
    placement: 'top',
    title: '🆘 Help Tickets',
    body: "Help tickets submitted by users appear here. Click the 💬 Chat button on a ticket to open a direct chat with that user. Tap anywhere to continue.",
    remeasureDelay: 400,
  },
  {
    id: 'tabs-feedback',
    target: '.admin-tabs',
    placement: 'bottom',
    title: '💭 Feedback Tab',
    body: "Click the Feedback tab to see user suggestions and feature requests.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'feedback-tab',
    target: '.admin-list, .admin-empty',
    placement: 'top',
    title: '💭 Feedback Tickets',
    body: "Feature requests and general feedback live here. You can change the status to Implementing, Implemented or Rejected to track progress. Tap anywhere to continue.",
    remeasureDelay: 400,
  },
  {
    id: 'tabs-users',
    target: '.admin-tabs',
    placement: 'bottom',
    title: '👥 Users Tab',
    body: "Click the Users tab to see and manage all registered users.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'users-tab',
    target: '.admin-list, .admin-empty',
    placement: 'top',
    title: '👥 User Management',
    body: "All users are listed here. You can view their profile with 👤 or Restrict/Unrestrict their trading access. Tap anywhere to continue.",
    remeasureDelay: 400,
  },
  {
    id: 'tabs-reports',
    target: '.admin-tabs',
    placement: 'bottom',
    title: '🚨 Reports Tab',
    body: "Click the Reports tab to see flagged trades.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'reports-tab',
    target: '.admin-list, .admin-empty',
    placement: 'top',
    title: '🚨 Trade Reports',
    body: "Reported trades appear here. You can Dismiss a report, Restrict a user or view their profile. Each action is logged automatically. Tap anywhere to continue.",
    remeasureDelay: 400,
  },
  {
    id: 'tabs-modlogs',
    target: '.admin-tabs',
    placement: 'bottom',
    title: '📋 Mod Logs Tab',
    body: "Click the Mod Logs tab to see every admin action taken.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'modlogs-tab',
    target: '.admin-list, .admin-empty',
    placement: 'top',
    title: '📋 Mod Logs',
    body: "Every admin action (restrict, dismiss, etc.) is recorded here with who did it, when, and why. Tap anywhere to continue.",
    remeasureDelay: 400,
  },
  {
    id: 'done',
    placement: 'center',
    title: '✅ All Set!',
    body: "You now know your way around the Admin Panel. Use it responsibly — every action is logged! 🏝️",
    actionLabel: 'Got it →',
  },
];

const USER_STEPS: Step[] = [
  {
    id: 'welcome',
    placement: 'center',
    title: '📋 My Activity',
    body: "This page shows your personal activity — any trades that were reported and your own help/feedback tickets. Tap anywhere to continue.",
    actionLabel: "Let's go →",
  },
  {
    id: 'trade-reports',
    target: '.admin-page',
    placement: 'center',
    title: '🚨 Trade Reports',
    body: "If any of your trades are flagged for review by the admin team, they'll appear here with the report reason and current status. Tap anywhere to continue.",
    remeasureDelay: 300,
  },
  {
    id: 'my-tickets',
    target: '.admin-page',
    placement: 'center',
    title: '🎫 My Tickets',
    body: "Your Help and Feedback tickets are listed here too. You can see their status and click 💬 Chat to message the admin team directly. Tap anywhere to continue.",
    remeasureDelay: 300,
  },
  {
    id: 'done',
    placement: 'center',
    title: '✅ Got It!',
    body: "That's all there is to this page. Use the Feedback page to submit new tickets anytime. 🏝️",
    actionLabel: 'Got it →',
  },
];

const PAD = 10;
const TOOLTIP_H_EST = 220;

interface SpotlightRect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

interface AdminTutorialProps {
  onComplete: () => void;
  onSwitchTab: (tab: 'reports' | 'help' | 'feedback' | 'users' | 'modlogs') => void;
  isFullAdmin: boolean;
}

export default function AdminTutorial({ onComplete, onSwitchTab, isFullAdmin }: AdminTutorialProps) {
  const STEPS = isFullAdmin ? ADMIN_STEPS : USER_STEPS;
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const measureTimers = useRef<number[]>([]);

  const step = STEPS[stepIdx];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  const measureTarget = useCallback((targetOverride?: string) => {
    const target = targetOverride || step.target;
    if (!target) { setRect(null); return; }
    measureTimers.current.forEach(clearTimeout);
    measureTimers.current = [];
    let attempts = 0;
    const tryMeasure = () => {
      const t = window.setTimeout(() => {
        const r = getRect(target);
        if (r) { setRect(r); }
        else if (attempts < 15) { attempts++; measureTimers.current.push(window.setTimeout(tryMeasure, 100)); }
        else setRect(null);
      }, 80);
      measureTimers.current.push(t);
    };
    tryMeasure();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.setAttribute('data-tutorial-step', step.id);
    return () => document.body.removeAttribute('data-tutorial-step');
  }, [step.id]);

  useEffect(() => {
    setVisible(false);
    setRect(null);
    if (step.onEnter) step.onEnter();
    const delay = step.remeasureDelay ? step.remeasureDelay + 100 : 300;
    const t = window.setTimeout(() => { measureTarget(); setVisible(true); }, delay);
    return () => clearTimeout(t);
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => { if (step.target) measureTarget(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measureTarget, step.target]);

  // Poll for tab-click waitForAction steps
  useEffect(() => {
    if (!step.waitForAction) return;

    if (step.id === 'tabs') {
      // Wait for user to click the Help tab
      const interval = window.setInterval(() => {
        const activeTab = document.querySelector('.admin-tab.active');
        if (activeTab?.textContent?.toLowerCase().includes('help')) {
          window.clearInterval(interval);
          setTimeout(() => setStepIdx(i => i + 1), 300);
        }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'tabs-feedback') {
      const interval = window.setInterval(() => {
        const activeTab = document.querySelector('.admin-tab.active');
        if (activeTab?.textContent?.toLowerCase().includes('feedback')) {
          window.clearInterval(interval);
          setTimeout(() => setStepIdx(i => i + 1), 300);
        }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'tabs-users') {
      const interval = window.setInterval(() => {
        const activeTab = document.querySelector('.admin-tab.active');
        if (activeTab?.textContent?.toLowerCase().includes('user')) {
          window.clearInterval(interval);
          setTimeout(() => setStepIdx(i => i + 1), 300);
        }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'tabs-reports') {
      const interval = window.setInterval(() => {
        const activeTab = document.querySelector('.admin-tab.active');
        if (activeTab?.textContent?.toLowerCase().includes('report')) {
          window.clearInterval(interval);
          setTimeout(() => setStepIdx(i => i + 1), 300);
        }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'tabs-modlogs') {
      const interval = window.setInterval(() => {
        const activeTab = document.querySelector('.admin-tab.active');
        if (activeTab?.textContent?.toLowerCase().includes('log') || activeTab?.textContent?.toLowerCase().includes('mod')) {
          window.clearInterval(interval);
          setTimeout(() => setStepIdx(i => i + 1), 300);
        }
      }, 300);
      return () => window.clearInterval(interval);
    }
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    if (stepIdx === STEPS.length - 1) {
      localStorage.setItem(TUTORIAL_DONE_KEY, '1');
      onComplete();
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const skip = () => {
    localStorage.setItem(TUTORIAL_DONE_KEY, '1');
    onComplete();
  };

  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect || step.placement === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', maxWidth: 'min(92vw,400px)', width: 'min(92vw,400px)' };
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const TW = Math.min(vw * 0.9, 370);
    const centreX = rect.left + rect.width / 2;
    const clampedLeft = Math.max(8, Math.min(centreX - TW / 2, vw - TW - 8));

    if (step.placement === 'bottom') {
      const top = rect.top + rect.height + PAD + 8;
      if (top + TOOLTIP_H_EST > vh - 8) return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
      return { position: 'fixed', top, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (step.placement === 'top') {
      return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (step.placement === 'right') {
      const left = Math.min(rect.left + rect.width + PAD + 8, vw - TW - 8);
      const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
      return { position: 'fixed', top, left, width: TW, maxWidth: TW };
    }
    const left = Math.max(8, rect.left - TW - PAD - 8);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
    return { position: 'fixed', top, left, width: TW, maxWidth: TW };
  };

  const spotlightStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
    borderRadius: 14,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    border: '1.5px solid rgba(255,140,30,0.7)',
    animation: 'tutorial-glow-pulse 1.8s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 200001,
    transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
  } : {
    position: 'fixed', top: 0, left: 0, width: 0, height: 0,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    pointerEvents: 'none', zIndex: 200001,
  };

  const renderBlocker = () => {
    const blockEvt = (e: React.MouseEvent | React.TouchEvent) => { e.stopPropagation(); e.preventDefault(); };
    if (!step.waitForAction || !rect) {
      return (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200000 }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchMove={e => e.preventDefault()}
        />
      );
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const ht = rect.top - PAD, hl = rect.left - PAD;
    const hb = rect.top + rect.height + PAD, hr = rect.left + rect.width + PAD;
    const s: React.CSSProperties = { position: 'fixed', zIndex: 200000, background: 'transparent' };
    return (
      <>
        <div style={{ ...s, top: 0, left: 0, width: vw, height: Math.max(0, ht) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: 0, width: Math.max(0, hl), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: hr, width: Math.max(0, vw - hr), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
      </>
    );
  };

  return (
    <div
      className={`tutorial-root${visible ? ' visible' : ''}`}
      data-tutorial-step={step.id}
      style={{ position: 'fixed', inset: 0, zIndex: 200000, pointerEvents: step.waitForAction ? 'none' : 'all' }}
    >
      {renderBlocker()}
      {!rect && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.76)', zIndex: 200001, pointerEvents: 'none' }} />}
      {rect && <div style={spotlightStyle} />}

      <div
        className="tutorial-card"
        style={{ ...getTooltipStyle(), pointerEvents: 'all', zIndex: 200002, transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), bottom 0.3s cubic-bezier(0.4,0,0.2,1)' }}
        onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
        onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
      >
        <div className="tutorial-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`tutorial-dot ${i === stepIdx ? 'active' : i < stepIdx ? 'done' : ''}`} />
          ))}
        </div>

        <h3 className="tutorial-title">{step.title}</h3>
        <p className="tutorial-body">{step.body}</p>

        <div className="tutorial-actions">
          <button className="tutorial-skip" onClick={skip}>Skip</button>
          {!step.waitForAction && stepIdx === STEPS.length - 1 && (
            <button className="tutorial-next" onClick={advance}>{step.actionLabel || 'Finish'}</button>
          )}
          {step.actionLabel && !step.waitForAction && stepIdx < STEPS.length - 1 && (
            <button className="tutorial-next" onClick={advance}>{step.actionLabel}</button>
          )}
          {!step.waitForAction && !step.actionLabel && stepIdx < STEPS.length - 1 && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>tap anywhere to continue</span>
          )}
        </div>

        <div className="tutorial-counter">{stepIdx + 1} / {STEPS.length}</div>
      </div>
    </div>
  );
}
